import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { GetRoles } from "../api/roles.api";
import useApiToken from "../hooks/useApiToken";
import { Role } from "../Models";
import RoleCard from "./Roles/RoleCard";
import NoData from "./Shared/NoData";
import { Spinner } from "./Shared/Spinner";

import RoleLeftSidebar, { Roleavg } from "./Roles/Sidebar/RoleLeftSideBar";
import { debounce } from "../helpers/constants";
import PaginationComponent from "./Shared/PaginationComponent";
import RoleRightSidebar from "./Roles/Sidebar/RoleRightSideBar";
import { ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import ToggleSwitch from "./Shared/ToggleSwitch";
import { clearLocalStorageExceptDark } from "./TextEditor/textEditorConfig";
import Loading from "./Shared/Loading";

export default function Roles() {
  const token = useApiToken();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(true);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<{ id: string, iscancel: boolean }[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[] | Roleavg[]>>({
    'status': [],
    'skills': [],
    'date': []
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortType, setSortType] = useState<"created" | "candidate" | "score" | "title">("created");
  const [sortDir, setSortDir] = useState<"asc" | "desc" | "default">("desc");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const itemsPerPage = 15;
  const isSearchingRef = useRef(false);

  const fetchRoles = async (params: any, query: string, page: number = currentPage) => {
    if (!token) { return; }
    try {
      const response = await GetRoles(params, sortType, sortDir, query, token || "", page, itemsPerPage);
      setRoles(response);
      setTotalCount((response as any)._totalCount ?? 0);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    fetchRoles(selectedFilters, searchQuery, currentPage);
  }, [sortType, sortDir, token])

  useEffect(() => {
    if (!token) return;
    if (isSearchingRef.current) return;  // skip if triggered by search reset
    setLoading(true);
    fetchRoles(selectedFilters, searchQuery, currentPage);
  }, [currentPage])

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // useEffect(() => {
  //   if (toggleRoleView) {
  //     setSelectedFilters({
  //       'status': [],
  //       'skills': [],
  //       'date': []
  //     })
  //     setSortDir('desc');
  //     setSortType('created');
  //     setSearchQuery('');
  //     fetchRoles({
  //       'status': [],
  //       'skills': [],
  //       'date': [],
  //     }, '');
  //   }
  // }, [toggleRoleView])

  const getFilterChange = async (filters: Record<string, string[] | Roleavg[]>, query: string) => {
    setLoading(true);
    const updatedFilters: Record<string, string[] | Roleavg[]> = { ...filters };
    setSearchQuery(query);
    if (filters.status && filters.status.length === 1 && filters.status[0] === 'all') {
      updatedFilters.status = [];
    } else if (filters.status) {
      updatedFilters.status = filters.status;
    }

    if (filters.skills && filters.skills.length === 1 && filters.skills[0] === 'all') {
      updatedFilters.skills = [];
    } else if (filters.skills) {
      updatedFilters.skills = filters.skills;
    }

    if (filters.department && filters.department.length === 1 && filters.department[0] === 'all') {
      updatedFilters.department = [];
    } else if (filters.department) {
      updatedFilters.department = filters.department;
    }

    if (
      Array.isArray(filters.date) &&
      filters.date.length > 0 &&
      typeof filters.date[0] !== 'string' &&
      filters.date[0].avgMin &&
      filters.date[0].avgMax
    ) {
      updatedFilters.date = filters.date;
    } else {
      updatedFilters.date = [];
    }
    setSelectedFilters(updatedFilters);
    isSearchingRef.current = true;   // flag: this page reset is from search
    setCurrentPage(1);
    await fetchRoles(updatedFilters, query, 1);
    isSearchingRef.current = false;  // reset after search fetch completes
  };


  const handleToggleLeftSidebar = (isOpen: any) => {
    setIsLeftSidebarOpen(isOpen);
  };
  // const handleToggleRightSidebar = (isOpen: any) => {
  //   setIsRightSidebarOpen(isOpen);

  // };

  const handleRoleSearch = (query: string) => {
    getFilterChange(selectedFilters, query);
  };


  const handleSelect = (roleId: string) => {
    setSelectedRoles(prev =>
      prev.some(role => role.id === roleId)
        ? prev.filter(role => role.id !== roleId)
        : [...prev, roles.map(({ id, iscancel }) => ({ id, iscancel })).find(role => role.id === roleId) || { id: roleId, iscancel: false }]
    );
  };


  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRoles(roles.map(({ id, iscancel }) => ({ id, iscancel })));
    } else {
      setSelectedRoles([]);
    }
  };

  const toggleSort = (type: "created" | "candidate" | "score" | "title") => {
    if (sortType === type) {
      setSortDir((prev) =>
        prev === "default" ? "asc" : prev === "asc" ? "desc" : "default"
      );
    } else {
      setSortType(type);
      setSortDir("asc");
    }
  };
  useEffect(() => {
    if (selectedRoles.length > 0) {
      setIsLeftSidebarOpen(true);
    }
  }, [selectedRoles])
  return (
    <>
      {/* {toggleRoleView ? <>
        <>
          <header className="bg-gray-100 py-8 dark:bg-darkbglight ">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-wrap">
              <h1 className="mt-2 text-2xl font-bold leading-7 text-gray-800 sm:text-3xl sm:tracking-tight dark:text-gray-300">
                Job Postings
              </h1>
              <div className="flex items-center">
                <Link to="/roles/calendar" className="mt-3 sm:ml-4 sm:mt-0">
                  <div className="flex items-center px-3 py-2 rounded-md  font-semibold text-gray-800 dark:text-gray-300 ">
                    <CalendarDaysIcon className="h-6 w-6 aria-hidden:true" />
                    <div className="pl-2 ">
                      Calendar
                    </div>
                  </div>
                </Link>
                <Link to="/roles/create" className="mt-3 sm:ml-4 sm:mt-0">
                  <button
                    type="button"
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={() => clearLocalStorageExceptDark()}
                  >
                    Create new job
                  </button>
                </Link>
              </div>
            </div>
          </header>
          {loading ? (
            <div className="h-[50vh] flex items-center justify-center">
              <Spinner height="h-14" width="h-14" />
            </div>
          ) : roles.length === 0 ? (
            <NoData
              title="No Roles"
              description="
      There are currently no available roles. Click 'Create new job' to start adding roles."
            />
          ) : (
            roles.map((role) => <RoleCard role={role} key={role.id} toggleView={toggleRoleView}
              isRightSidebarOpen={isRightSidebarOpen}
              selectedRoles={selectedRoles}
              handleSelect={handleSelect} />)
          )}
        </>
      </> : <> */}
      <>
        <div className="relative overflow-x-auto sm:rounded-lg min-h-[87vh]">
          <div className={`transition-all duration-200 ${isLeftSidebarOpen ? "pl-[19rem]" : "pl-0"} ${isRightSidebarOpen ? "pr-[19rem]" : "pl-0"}`}>
            {loading ? (
              <div className="h-[50vh] flex items-center justify-center">
                <Spinner height="h-10" width="h-10" />
              </div>
            ) : roles.length === 0 ? (
              <NoData
                title={searchQuery ? "No results found for your search" : "No Roles"}
                description={searchQuery ? `No roles matched "${searchQuery}". Try a different search term or clear the search.` : "There are currently no available roles. Click 'Create new job' to start adding roles."}
              />
            ) : (
              <div className="overflow-x-hidden overflow-y-auto shadow-md sm:rounded-lg">

                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead className="bg-white dark:bg-darkbglight z-20">
                    <tr>
                      <th scope="col" className="px-4 pt-2 w-[2%]">
                        <input
                          type="checkbox"
                          checked={selectedRoles.length === roles.length}
                          onChange={handleSelectAll}
                          className="form-checkbox left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </th>
                      <th scope="col" className="py-3 w-[20%] flex flex-row items-center gap-2">
                        <div className="flex items-center justify-center">
                          Title
                          {sortType === "title" && sortDir === "asc" ? (
                            <ArrowUpIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("title")}
                            />
                          ) : sortType === "title" && sortDir === "desc" ? (
                            <ArrowDownIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("title")}
                            />
                          ) : (
                            <ArrowsUpDownIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("title")}
                            />
                          )}
                        </div>
                      </th>
                      <th scope="col" className="py-3 w-[5%] hidden sm:table-cell">
                        <div className="flex items-center justify-center">Salary
                        </div>
                      </th>
                      <th scope="col" className="py-3 w-[12%] hidden sm:table-cell">
                        <div className="flex items-center justify-center">Total Candidates
                          {sortType === "candidate" && sortDir === "asc" ? (
                            <ArrowUpIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("candidate")}
                            />
                          ) : sortType === "candidate" && sortDir === "desc" ? (
                            <ArrowDownIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("candidate")}
                            />
                          ) : (
                            <ArrowsUpDownIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("candidate")}
                            />
                          )}
                        </div>
                      </th>
                      <th scope="col" className="py-3 w-[9%] hidden sm:table-cell">
                        <div className="flex items-center justify-center">Avg Score
                          {sortType === "score" && sortDir === "asc" ? (
                            <ArrowUpIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("score")}
                            />
                          ) : sortType === "score" && sortDir === "desc" ? (
                            <ArrowDownIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("score")}
                            />
                          ) : (
                            <ArrowsUpDownIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("score")}
                            />
                          )}

                        </div>
                      </th>
                      <th scope="col" className="py-3 w-[24%] hidden sm:table-cell">
                        Skills
                      </th>
                      <th scope="col" className="py-3 w-[10%] hidden sm:table-cell">
                        <div className="flex items-center justify-center">Location</div>
                      </th>
                      <th scope="col" className="py-3 w-[8%] hidden sm:table-cell">
                        <div className="flex items-center justify-center">Created
                          {sortType === "created" && sortDir === "asc" ? (
                            <ArrowUpIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("created")}
                            />
                          ) : sortType === "created" && sortDir === "desc" ? (
                            <ArrowDownIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("created")}
                            />
                          ) : (
                            <ArrowsUpDownIcon
                              className="w-3 h-3 ml-1 text-gray-500 cursor-pointer"
                              onClick={() => toggleSort("created")}
                            />
                          )}

                        </div>
                      </th>
                      <th scope="col" className="py-3 w-[7%] hidden sm:table-cell">
                        <div className="flex items-center justify-center">Status</div>
                      </th>
                      <th scope="col" className="px-6 py-3 w-[5%]">
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((role) => (
                      <RoleCard
                        key={role.id}
                        role={role}
                        isRightSidebarOpen={isRightSidebarOpen}
                        selectedRoles={selectedRoles}
                        handleSelect={handleSelect}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <RoleLeftSidebar onToggle={handleToggleLeftSidebar} handleRoleSearch={handleRoleSearch} currentPage={currentPage} getFilterChange={getFilterChange} isOpen={isLeftSidebarOpen} selectedRoles={selectedRoles} setLoading={setSidebarLoading} />
          {/* <RoleRightSidebar onToggle={handleToggleRightSidebar} handleRoleSearch={handleRoleSearch} currentPage={1} selectedRoles={selectedRoles} isOpen={isRightSidebarOpen} setLoading={setSidebarLoading} /> */}

        </div>
        {(totalCount > itemsPerPage && roles.length > 0) && (
          <div className={`${isLeftSidebarOpen ? "pl-[19rem]" : "pl-0"} ${isRightSidebarOpen ? "pr-[19rem]" : "pl-0"}`}>
            <PaginationComponent
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / itemsPerPage)}
              handlePageChange={handlePageChange}
            />
          </div>
        )}
        {sidebarLoading &&
          <div className='fixed z-50 top-0 flex items-center justify-center w-full h-full bg-gray-400  bg-opacity-50 dark:bg-opacity-20 transition-opacity '>
            <Loading />
          </div>
        }
      </>
    </>
  );
}
